// #417 / #687 — Mobile-menu state selector (collapsible list variant).
//
// This is a static-Liquid + vanilla-JS counterpart to the Vue
// `state-selector` component. The two render slots (compact dropdown for
// announcement bar / desktop utility area vs. collapsible list at the bottom
// of the mobile menu) have different enough UX that sharing one Vue template
// caused styling fights against v-show / display:none. Static `<li>`s in the
// menu also inherit the existing menu-item styling for free.
//
// Contract:
//   - The Vue `state-selector`'s `tena:select-state` listener handles
//     persistence and state propagation. This module just dispatches that
//     event when a row is tapped — the Vue instance does the rest.
//   - On state changes (`tena:state-selected`, `tena:state-detected`,
//     `tena:state-cleared`) we update the trigger label and active row.
//
// DOM contract (rendered in theme/snippets/layout-mobile-header.liquid):
//   <li data-state-selector-menu>
//     <span data-state-selector-menu-current></span>
//   </li>
//   <li data-state-selector-menu-option="NSW">…</li>
//   … (8 options, hidden until trigger has `.is-open`)

import { GlobalState } from '../../global-state/global-state';
import { AU_STATE_CODES } from '../../utils/geo-state';
import { STATE_EVENTS } from '../../utils/state-events';

const ACTIVE_CLASS = 'is-active';
const OPEN_CLASS = 'is-open';

function currentEffectiveState() {
  const s = GlobalState && GlobalState.state;
  if (!s) return '';
  return s.selectedState || s.detectedState || '';
}

function updateUi() {
  const state = currentEffectiveState();
  const currentEl = document.querySelector('[data-state-selector-menu-current]');
  if (currentEl) currentEl.textContent = state || '—';

  const options = document.querySelectorAll('[data-state-selector-menu-option]');
  options.forEach((el) => {
    const code = el.getAttribute('data-state-selector-menu-option');
    el.classList.toggle(ACTIVE_CLASS, code === state);
    el.setAttribute('aria-selected', code === state ? 'true' : 'false');
  });
}

export default function StateSelectorMenu() {
  const trigger = document.querySelector('[data-state-selector-menu]');
  if (!trigger) return; // Mobile menu render slot not on this page
  // Guard against re-init (HMR, partial re-render) — listeners on document
  // and window would otherwise stack.
  if (trigger.dataset.bound === '1') return;
  trigger.dataset.bound = '1';

  trigger.setAttribute('role', 'button');
  trigger.setAttribute('tabindex', '0');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  // Bind the trigger to its 8 sibling option <li>s so AT can navigate from
  // the disclosure trigger to the listbox even though they aren't wrapped
  // in a `role="listbox"` container (the options sit inside the parent
  // menu <ul> for styling reasons).
  const optionIds = AU_STATE_CODES
    .map((code) => `state-selector-menu-option-${code}`)
    .join(' ');
  trigger.setAttribute('aria-owns', optionIds);

  const setOpen = (open) => {
    trigger.classList.toggle(OPEN_CLASS, open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  };
  const toggleOpen = () => setOpen(!trigger.classList.contains(OPEN_CLASS));

  trigger.addEventListener('click', toggleOpen);
  trigger.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOpen(); }
    if (e.key === 'Escape') { setOpen(false); }
  });
  // Esc anywhere while open also closes — matches the standard disclosure pattern.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && trigger.classList.contains(OPEN_CLASS)) setOpen(false);
  });

  const options = document.querySelectorAll('[data-state-selector-menu-option]');
  options.forEach((el) => {
    el.setAttribute('role', 'option');
    el.setAttribute('tabindex', '0');
    el.addEventListener('click', () => {
      const code = el.getAttribute('data-state-selector-menu-option');
      if (!code || !AU_STATE_CODES.includes(code)) return;
      window.dispatchEvent(new CustomEvent(STATE_EVENTS.SELECT, {
        detail: { state: code },
      }));
      // Collapse so the user immediately sees the updated label without
      // having to tap the caret again.
      setOpen(false);
    });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.click(); }
    });
  });

  // Re-render on any state change emitted by the rest of the system.
  window.addEventListener(STATE_EVENTS.SELECTED, updateUi);
  window.addEventListener(STATE_EVENTS.DETECTED, updateUi);
  window.addEventListener(STATE_EVENTS.CLEARED, updateUi);

  // Initial paint — detection cascade may complete before or after this
  // module loads, so we paint now and let the listeners catch any later
  // change.
  updateUi();
}
